import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { CheckCircle2, Loader2, Crown, AlertTriangle, Check, Clock } from "lucide-react";

export default function PaymentSuccessDialog({ sessionId, onClose, API, getAuthConfig, availablePlans = [], onConfirmed }) {
  const [state, setState] = useState("verifying");
  const [details, setDetails] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    setState("verifying");
    setDetails(null);
    attempts.current = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, getAuthConfig());
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setDetails(res.data);
          setState("success");
          onConfirmed?.();
          return;
        }
        if (attempts.current >= 8) { setState("processing"); return; }
        setTimeout(poll, 2500);
      } catch {
        if (cancelled) return;
        if (attempts.current >= 3) { setState("error"); return; }
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (!sessionId) return null;

  const plan = availablePlans.find((p) => p.plan_id === details?.plan_id);
  const planName = plan?.name || (details?.plan_id ? details.plan_id.charAt(0).toUpperCase() + details.plan_id.slice(1) : "your plan");
  const amount = details?.amount_total != null ? details.amount_total / 100 : null;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="payment-success-dialog">
        {state === "verifying" && (
          <div className="flex flex-col items-center text-center py-8" data-testid="payment-verifying">
            <Loader2 className="w-10 h-10 text-[#FFCC00] animate-spin mb-4" />
            <DialogTitle className="text-lg font-semibold text-foreground font-['Manrope']">Confirming your payment…</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">Hang tight — we're verifying the payment with Stripe. This usually takes a few seconds.</p>
          </div>
        )}

        {state === "success" && (
          <div className="flex flex-col items-center text-center py-4" data-testid="payment-success-content">
            <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-green-500" />
            </div>
            <DialogTitle className="text-xl font-bold text-foreground font-['Manrope']">Payment Successful!</DialogTitle>
            <p className="text-sm text-muted-foreground mt-1 mb-5">Your subscription is now active. Welcome aboard!</p>

            <div className="w-full rounded-xl border border-theme bg-theme-surface p-4 space-y-2.5 text-left">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Plan</span>
                <span className="font-semibold text-foreground flex items-center gap-1.5" data-testid="payment-success-plan">
                  <Crown className="w-4 h-4 text-[#FFCC00]" /> {planName}
                </span>
              </div>
              {amount != null && amount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Amount Paid</span>
                  <span className="font-semibold text-green-500" data-testid="payment-success-amount">${amount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <Badge className="bg-green-500/20 text-green-500 border-green-500/30">Active</Badge>
              </div>
              {(plan?.features || []).slice(0, 4).length > 0 && (
                <div className="pt-2 border-t border-theme space-y-1.5">
                  {(plan.features || []).slice(0, 4).map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Check className="w-3.5 h-3.5 text-green-500 shrink-0" /> {f}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={onClose} className="btn-primary w-full mt-5 h-10" data-testid="payment-success-close-btn">
              Start Using {planName}
            </Button>
          </div>
        )}

        {state === "processing" && (
          <div className="flex flex-col items-center text-center py-8" data-testid="payment-processing">
            <div className="w-14 h-14 rounded-full bg-amber-500/15 flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
            <DialogTitle className="text-lg font-semibold text-foreground font-['Manrope']">Payment is processing</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">Stripe is still confirming your payment. Your plan will activate automatically once it clears — check back in a minute.</p>
            <Button variant="outline" onClick={onClose} className="mt-5" data-testid="payment-processing-close-btn">Close</Button>
          </div>
        )}

        {state === "error" && (
          <div className="flex flex-col items-center text-center py-8" data-testid="payment-error">
            <div className="w-14 h-14 rounded-full bg-red-500/15 flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <DialogTitle className="text-lg font-semibold text-foreground font-['Manrope']">Couldn't verify the payment</DialogTitle>
            <p className="text-sm text-muted-foreground mt-2">We couldn't confirm the payment status right now. If you completed the payment, your plan will activate shortly — check My Plan in a minute or contact support.</p>
            <Button variant="outline" onClick={onClose} className="mt-5" data-testid="payment-error-close-btn">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
